import React, { useEffect, useState } from 'react';
import { X, ExternalLink, BookOpen, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { getEvidence } from '../services/api';

export default function EvidenceDrawer({ open, onClose, generic, evidence: initialEvidence, drugId }) {
  const [evidence, setEvidence] = useState(initialEvidence || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchEvidence() {
      if (!open || !generic) return;
      if (initialEvidence?.length > 0) {
        setEvidence(initialEvidence.filter(e => 
          e.generic_id === generic.id || e.drug_id === drugId
        ));
        return;
      }
      
      setLoading(true);
      try {
        const res = await getEvidence({ generic_id: generic.id, drug_id: drugId });
        setEvidence(res.data.evidence || []);
      } catch (error) {
        console.error('Failed to fetch evidence:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchEvidence();
  }, [open, generic, drugId, initialEvidence]);

  if (!open) return null;

  const getConfidenceBadge = (level) => {
    const badges = {
      'High': { class: 'badge-success', icon: CheckCircle },
      'Moderate': { class: 'badge-warning', icon: Info },
      'Low': { class: 'badge-danger', icon: AlertTriangle }
    };
    return badges[level] || badges['Moderate'];
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40"
        onClick={onClose}
        data-testid="evidence-drawer-overlay"
      />
      
      {/* Drawer */}
      <div 
        className="fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-2xl border-l border-slate-200 z-50 animate-slide-in overflow-hidden flex flex-col"
        data-testid="evidence-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <BookOpen className="w-5 h-5 text-[#0A2540]" />
            <h2 className="text-lg font-semibold text-slate-900">Evidence Library</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="close-drawer-btn"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Generic Summary */}
        {generic && (
          <div className="p-4 bg-slate-50 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">{generic.name}</h3>
            <p className="text-sm text-slate-500">{generic.manufacturer}</p>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-sm">
                Confidence: <span className="font-medium">{generic.confidence}%</span>
              </span>
              <span className="text-sm">
                Overall Score: <span className="font-medium">{generic.overall_score}/100</span>
              </span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="p-4 bg-slate-50 rounded-lg">
                  <div className="skeleton h-5 w-3/4 mb-2" />
                  <div className="skeleton h-4 w-1/2 mb-3" />
                  <div className="skeleton h-12 w-full" />
                </div>
              ))}
            </div>
          ) : evidence.length > 0 ? (
            <div className="space-y-4">
              {evidence.map(ev => {
                const badge = getConfidenceBadge(ev.confidence_level);
                const BadgeIcon = badge.icon;
                
                return (
                  <div key={ev.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    {/* Title & Meta */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 pr-4">
                        <h4 className="font-medium text-slate-900 leading-tight">{ev.title}</h4>
                        <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                          <span className="badge-neutral">{ev.study_type}</span>
                          <span>{ev.year}</span>
                          <span>•</span>
                          <span>{ev.source_type}</span>
                        </div>
                      </div>
                      {ev.citation_url && (
                        <a 
                          href={ev.citation_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                        >
                          <ExternalLink className="w-4 h-4 text-slate-400" />
                        </a>
                      )}
                    </div>

                    {/* Population */}
                    {ev.population && (
                      <p className="text-sm text-slate-600 mb-3">
                        <span className="font-medium">Population:</span> {ev.population}
                      </p>
                    )}

                    {/* Key Takeaway */}
                    <div className="p-3 bg-white rounded-lg border border-slate-200 mb-3">
                      <p className="text-sm font-medium text-slate-700 mb-1">Key Takeaway</p>
                      <p className="text-sm text-slate-600">{ev.key_takeaway}</p>
                    </div>

                    {/* Confidence */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <BadgeIcon className="w-4 h-4" />
                        <span className={badge.class}>{ev.confidence_level} Confidence</span>
                      </div>
                      <span className="text-xs text-slate-400">{ev.source_name}</span>
                    </div>

                    {/* Why it matters */}
                    {ev.why_it_matters && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <p className="text-sm font-medium text-slate-700 mb-1">Why This Matters</p>
                        <p className="text-sm text-slate-600">{ev.why_it_matters}</p>
                      </div>
                    )}

                    {/* Evidence gaps */}
                    {ev.evidence_gaps && (
                      <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
                          <AlertTriangle className="w-4 h-4" />
                          Evidence Gaps
                        </div>
                        <p className="text-sm text-amber-600 mt-1">{ev.evidence_gaps}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 font-medium">No evidence available</p>
              <p className="text-sm text-slate-500 mt-1">
                Evidence entries for this generic will appear here
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <p className="text-xs text-slate-500 text-center">
            Evidence is provided for decision support only. All substitution decisions require clinical review.
          </p>
        </div>
      </div>
    </>
  );
}
